import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, Share2, Mail, ArrowLeft, Star } from "lucide-react";
import { getPublicBill, getPublicConfig, fmt, fmtDate, fmtDateLong, LOGO_URL, BRAND_NAME } from "@/lib/api";
import { toast } from "sonner";

const METHOD_LABEL = { cash: "Cash", card: "Credit Card", transfer: "Bank Transfer", other: "Other" };

export default function Bill() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ google_review_url: "" });

  useEffect(() => {
    getPublicBill(id).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    getPublicConfig().then(setConfig).catch(() => {});
  }, [id]);

  const publicUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/bill/${id}`;
  }, [id]);

  const handlePrint = () => window.print();

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Account summary · ${data?.guest_name}`,
          text: `Your stay bill at Reception PMS`,
          url: publicUrl,
        });
      } else {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Account summary link copied to clipboard");
      }
    } catch {
      /* user cancelled or no permission */
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading bill...</div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Account summary not found.</div>;
  }

  const s = data.summary;
  const paidFull = s && s.remaining <= 0.01 && s.total > 0;
  const consumptionsByCat = (data.consumptions || []).reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});
  const catOrder = ["bar", "activities", "shop"];
  const catLabel = { bar: "Bar", activities: "Activities", shop: "Shop" };

  const today = fmtDateLong(new Date().toISOString().slice(0, 10));

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Top actions — hidden on print */}
      <div className="print:hidden bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/reservations")}
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
          data-testid="bill-back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleShare} className="h-10" data-testid="bill-share-btn">
            <Share2 size={14} /> Share
          </Button>
          <Button onClick={handlePrint} className="h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="bill-print-btn">
            <Printer size={14} /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-[800px] mx-auto my-6 bg-white shadow-sm print:shadow-none print:my-0 print:max-w-none" data-testid="bill-sheet">
        <div className="p-10 print:p-8">
          {/* Header */}
          <div className="flex items-start justify-between pb-6 border-b-2 border-slate-900">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-md bg-slate-950 flex items-center justify-center p-1.5 shrink-0">
                <img src={LOGO_URL} alt={BRAND_NAME} className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl text-slate-900 leading-tight">{BRAND_NAME}</div>
                <div className="text-xs text-slate-500 mt-1">Account Summary</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500">Account Summary</div>
              <div className="font-heading font-bold text-lg text-slate-900" data-testid="bill-receipt-number">#{data.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-xs text-slate-500 mt-0.5">{today}</div>
            </div>
          </div>

          {/* Guest + QR */}
          <div className="grid grid-cols-3 gap-6 py-6 border-b border-slate-200">
            <div className="col-span-2 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Billed to</div>
                <div className="font-heading font-bold text-xl text-slate-900">{data.guest_name}</div>
                {data.guest_email && (
                  <div className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                    <Mail size={13} className="text-slate-400" /> {data.guest_email}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Unit</div>
                  <div className="text-sm font-medium text-slate-900 mt-1">{data.unit?.display_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Check-in</div>
                  <div className="text-sm font-medium text-slate-900 mt-1">{fmtDate(data.check_in)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Check-out</div>
                  <div className="text-sm font-medium text-slate-900 mt-1">{fmtDate(data.check_out)}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end justify-start">
              <div className="p-2 border border-slate-200 rounded-md bg-white">
                <QRCodeCanvas value={publicUrl} size={96} level="M" />
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5 max-w-[120px] text-right leading-tight">Scan to open this bill on any device</div>
            </div>
          </div>

          {/* Line items */}
          <div className="py-6">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Charges</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 font-medium text-slate-500 text-xs uppercase tracking-wider">Description</th>
                  <th className="text-center py-2 font-medium text-slate-500 text-xs uppercase tracking-wider w-16">Qty</th>
                  <th className="text-right py-2 font-medium text-slate-500 text-xs uppercase tracking-wider w-24">Unit</th>
                  <th className="text-right py-2 font-medium text-slate-500 text-xs uppercase tracking-wider w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {s?.accommodation_total > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="py-2.5">
                      <div className="font-medium text-slate-900">Accommodation</div>
                      <div className="text-xs text-slate-500">{fmtDate(data.check_in)} → {fmtDate(data.check_out)}</div>
                    </td>
                    <td className="py-2.5 text-center text-slate-600">1</td>
                    <td className="py-2.5 text-right text-slate-600">{fmt(s.accommodation_total)}</td>
                    <td className="py-2.5 text-right font-medium text-slate-900">{fmt(s.accommodation_total)}</td>
                  </tr>
                )}
                {catOrder.map(cat => {
                  const items = consumptionsByCat[cat];
                  if (!items?.length) return null;
                  return items.map((c, idx) => (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="py-2.5">
                        <div className="font-medium text-slate-900">{c.product_name}</div>
                        {idx === 0 && <div className="text-xs text-slate-500">{catLabel[cat]}</div>}
                      </td>
                      <td className="py-2.5 text-center text-slate-600">{c.quantity}</td>
                      <td className="py-2.5 text-right text-slate-600">{fmt(c.unit_price)}</td>
                      <td className="py-2.5 text-right font-medium text-slate-900">{fmt(c.total)}</td>
                    </tr>
                  ));
                })}
                {(!s?.accommodation_total && !data.consumptions?.length) && (
                  <tr><td colSpan={4} className="py-4 text-center text-slate-400 text-xs">No charges</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end pb-6 border-b border-slate-200">
            <div className="w-64 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Accommodation</span>
                <span className="text-slate-900">{fmt(s?.accommodation_total || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Consumptions</span>
                <span className="text-slate-900">{fmt(s?.consumptions_total || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-heading font-semibold text-slate-900">Total</span>
                <span className="font-heading font-bold text-slate-900">{fmt(s?.total || 0)}</span>
              </div>
              <div className="flex justify-between text-emerald-700">
                <span>Paid</span>
                <span>-{fmt(s?.paid || 0)}</span>
              </div>
              <div className={`flex justify-between pt-2 border-t-2 border-slate-900 ${paidFull ? "text-emerald-600" : "text-red-600"}`}>
                <span className="font-heading font-bold">{paidFull ? "Paid in full" : "Balance due"}</span>
                <span className="font-heading font-bold text-lg">{fmt(Math.max(0, s?.remaining || 0))}</span>
              </div>
            </div>
          </div>

          {/* Payments list */}
          {data.payments?.length > 0 && (
            <div className="py-6 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Payments received</div>
              <div className="space-y-1 text-sm">
                {data.payments.map(p => (
                  <div key={p.id} className="flex justify-between text-slate-600">
                    <span>{fmtDate(p.date)} · {METHOD_LABEL[p.method] || p.method}</span>
                    <span className="font-medium text-slate-900">{fmt(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoice request notice — printable */}
          <div className="pt-6" data-testid="bill-invoice-notice">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Need an invoice?</div>
            <div className="text-xs text-slate-600 leading-relaxed">
              This document is an <span className="font-semibold text-slate-900">account summary</span>, not an invoice. If you would like an invoice, please email{" "}
              <a href="mailto:info@ondinaguesthouse.com" className="font-semibold text-slate-900 underline">info@ondinaguesthouse.com</a>{" "}
              with your name, address, and tax file number.
            </div>
          </div>

          {/* Google review CTA — hidden on print */}
          {config.google_review_url && (
            <div className="pt-6 print:hidden" data-testid="bill-review-cta">
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-center">
                <div className="text-xl tracking-[0.3em] text-amber-500 mb-2">★ ★ ★ ★ ★</div>
                <div className="font-heading font-bold text-slate-900">Enjoyed your stay?</div>
                <div className="text-xs text-slate-600 mt-1 mb-4">A 1-minute Google review helps us welcome more guests.</div>
                <a
                  href={config.google_review_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2.5 rounded-md text-sm transition-colors"
                  data-testid="bill-review-link"
                >
                  <Star size={14} fill="currentColor" /> Leave a Google review
                </a>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="pt-8 mt-4 text-center text-xs text-slate-400 border-t border-slate-100">
            Thank you for staying with us · {publicUrl}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
